// ─── ALQOS™ Notificador de Usuarios ──────────────────────────
// Envía emails automáticos ante eventos críticos del sistema
// Requiere EmailJS configurado en variables de entorno de Vercel
import emailjs from "@emailjs/browser";
import { supabase } from "./supabase";
import { subirDatos, eliminarDato } from "./syncSupabase"; // eliminarDato agregado — necesario para limpiar notificaciones viejas del mismo tipo, tanto local como en Supabase
import { calcularTodasLasAlertasReposicion } from "./StockMateriales";
const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE || "";
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE || "";
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_KEY || "";
const LOG = {
  info: (msg, data) => console.log(`[NOTIF][INFO] ${msg}`, data || ""),
  error: (msg, err) => console.error(`[NOTIF][ERROR] ${msg}`, err || ""),
};
// ─── STORAGE DE NOTIFICACIONES ────────────────────────────────
const STORAGE_NOTIF = "alqos_notificaciones";
export function guardarNotificacion(notif) {
  try {
    const todas = JSON.parse(localStorage.getItem(STORAGE_NOTIF) || "[]");
    const nueva = { ...notif, id: Date.now(), fecha: new Date().toISOString(), leida: false };
    localStorage.setItem(STORAGE_NOTIF, JSON.stringify([nueva, ...todas].slice(0, 200)));
    window.dispatchEvent(new Event("alqos-notif-nueva"));
    // CAMBIO: antes llamaba directo a supabase.from("notificaciones").upsert(...), lo que
    // no incluía tenant_id y rompía el guardado con error 23502 (not-null constraint).
    // subirDatos() es la misma función que usa el resto de ALQOS y ya sabe agregarlo.
    subirDatos(STORAGE_NOTIF);
    return nueva;
  } catch (e) { return null; }
}
export function getNotificaciones() {
  try { return JSON.parse(localStorage.getItem(STORAGE_NOTIF) || "[]"); }
  catch { return []; }
}
export function marcarLeida(id) {
  try {
    const todas = JSON.parse(localStorage.getItem(STORAGE_NOTIF) || "[]");
    const actualizadas = todas.map(n => n.id === id ? { ...n, leida: true } : n);
    localStorage.setItem(STORAGE_NOTIF, JSON.stringify(actualizadas));
  } catch (e) { LOG.error("Error marcar leída", e); }
}
export function marcarTodasLeidas() {
  try {
    const todas = JSON.parse(localStorage.getItem(STORAGE_NOTIF) || "[]");
    localStorage.setItem(STORAGE_NOTIF, JSON.stringify(todas.map(n => ({ ...n, leida: true }))));
  } catch (e) { LOG.error("Error marcar todas leídas", e); }
}
// ─── LIMPIAR notificaciones viejas de un tipo antes de regenerar ──
// Evita que verificarAlertasOperaciones() acumule una notificación nueva cada vez que
// corre (cada login, cada 15 min) sin nunca borrar las anteriores del mismo tipo, y evita
// que una alerta ya resuelta (OT borrada, Acción Correctiva cerrada) siga apareciendo para
// siempre. Borra tanto de localStorage como de Supabase (eliminarDato), porque si solo se
// borrara localmente, el próximo syncNotificacionesDesdeSupabase() la volvería a traer del
// servidor y "resucitaría" la notificación vieja.
function limpiarNotificacionesTipo(tipo) {
  try {
    const todas = JSON.parse(localStorage.getItem(STORAGE_NOTIF) || "[]");
    const aBorrar = todas.filter(n => n.tipo === tipo);
    const restantes = todas.filter(n => n.tipo !== tipo);
    localStorage.setItem(STORAGE_NOTIF, JSON.stringify(restantes));
    aBorrar.forEach(n => eliminarDato(STORAGE_NOTIF, n.id));
  } catch (e) { LOG.error(`Error limpiando notificaciones tipo ${tipo}`, e); }
}
// ─── ENVÍO DE EMAIL ───────────────────────────────────────────
async function enviarEmail(params) {
  if (true || !SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
    LOG.info("EmailJS no configurado — notificación solo en sistema", params);
    return false;
  }
  try {
    await emailjs.send(SERVICE_ID, TEMPLATE_ID, params, PUBLIC_KEY);
    LOG.info("Email enviado", { destinatario: params.to_email });
    return true;
  } catch (err) {
    LOG.error("Error al enviar email", err);
    return false;
  }
}
// ─── EVENTOS DE NOTIFICACIÓN ──────────────────────────────────
/**
 * Notifica cuando se genera una NC nueva
 * @param {object} nc — objeto NC completo
 * @param {string} emailResponsable — email del responsable
 * @param {object} usuarioCreador — usuario que creó la NC
 */
export async function notificarNCAbierta(nc, emailResponsable, usuarioCreador) {
  const notif = guardarNotificacion({
    tipo: "nc_abierta",
    titulo: `⚠️ Nueva NC generada: ${nc.nci}`,
    mensaje: `Se registró una No Conformidad ${nc.severidad === "critica" ? "CRÍTICA" : ""} en ${nc.proceso}. Severidad: ${nc.severidad}. Acción requerida.`,
    idRelacionado: nc.nci,
    modulo: "NC",
    color: nc.severidad === "critica" ? "#e63946" : "#f59e0b",
  });
  await enviarEmail({
    to_email: emailResponsable,
    to_name: emailResponsable,
    subject: `ALQOS™ — Nueva NC ${nc.severidad === "critica" ? "CRÍTICA" : ""}: ${nc.nci}`,
    titulo_notif: `Nueva No Conformidad registrada`,
    mensaje_principal: `Se registró la NC ${nc.nci} en el proceso "${nc.proceso}".`,
    detalle: `Descripción: ${nc.descripcion || "—"} | Severidad: ${nc.severidad} | Origen: ${nc.origen}`,
    accion_requerida: `Ingresá a ALQOS™ para revisar y asignar acción correctiva.`,
    link_sistema: "https://alqos.vercel.app",
    creado_por: usuarioCreador?.nombre || "Sistema",
    fecha: new Date().toLocaleString("es-AR"),
    modulo: "No Conformidades",
  });
  return notif;
}
/**
 * Notifica cuando una Acción Correctiva está por vencer o ya venció
 * @param {object} ac — objeto Acción Correctiva
 * @param {string} emailResponsable — email del responsable
 */
export async function notificarACVencida(ac, emailResponsable) {
  const diasRestantes = Math.ceil((new Date(ac.fechaCompromiso) - new Date()) / (1000 * 60 * 60 * 24));
  const vencida = diasRestantes < 0;
  const notif = guardarNotificacion({
    tipo: vencida ? "ac_vencida" : "ac_por_vencer",
    titulo: vencida ? `⏰ Acción Correctiva vencida: ${ac.idAccion}` : `⚠️ Acción Correctiva por vencer: ${ac.idAccion}`,
    mensaje: vencida
      ? `La acción correctiva ${ac.idAccion} venció hace ${Math.abs(diasRestantes)} día(s). NC vinculada: ${ac.idNC}`
      : `La acción correctiva ${ac.idAccion} vence en ${diasRestantes} día(s). NC vinculada: ${ac.idNC}`,
    idRelacionado: ac.idAccion,
    modulo: "AccionesCorrectivas",
    color: vencida ? "#e63946" : "#f59e0b",
  });
  await enviarEmail({
    to_email: emailResponsable,
    to_name: ac.responsable || emailResponsable,
    subject: vencida
      ? `ALQOS™ — VENCIDA: Acción Correctiva ${ac.idAccion}`
      : `ALQOS™ — Vence en ${diasRestantes} días: ${ac.idAccion}`,
    titulo_notif: vencida ? "Acción Correctiva vencida" : "Acción Correctiva por vencer",
    mensaje_principal: `La acción correctiva ${ac.idAccion} vinculada a la NC ${ac.idNC} ${vencida ? "ha vencido" : `vence en ${diasRestantes} día(s)`}.`,
    detalle: `Descripción: ${ac.descripcionAccion || "—"} | Responsable: ${ac.responsable} | Fecha compromiso: ${ac.fechaCompromiso}`,
    accion_requerida: "Ingresá a ALQOS™ para actualizar el estado de la acción correctiva.",
    link_sistema: "https://alqos.vercel.app",
    creado_por: "Sistema ALQOS™",
    fecha: new Date().toLocaleString("es-AR"),
    modulo: "Acciones Correctivas",
  });
  return notif;
}
/**
 * Notifica cuando hay una falla correctiva TPM
 * @param {object} tpm — objeto registro TPM
 * @param {string} emailResponsable — email del responsable
 * @param {object} usuario — usuario que registró
 */
export async function notificarFallaTPM(tpm, emailResponsable, usuario) {
  const notif = guardarNotificacion({
    tipo: "tpm_falla",
    titulo: `🔧 Falla correctiva registrada: ${tpm.idTPM}`,
    mensaje: `Equipo: ${tpm.maquina}. ${tpm.descripcion?.slice(0, 80) || ""}`,
    idRelacionado: tpm.idTPM,
    modulo: "TPM",
    color: "#e63946",
  });
  await enviarEmail({
    to_email: emailResponsable,
    to_name: tpm.responsable || emailResponsable,
    subject: `ALQOS™ — Falla correctiva en ${tpm.maquina}: ${tpm.idTPM}`,
    titulo_notif: "Falla correctiva registrada en TPM",
    mensaje_principal: `Se registró una falla correctiva en el equipo "${tpm.maquina}".`,
    detalle: `Descripción: ${tpm.descripcion || "—"} | Sector: ${tpm.sector || "—"} | Responsable: ${tpm.responsable}`,
    accion_requerida: "Ingresá a ALQOS™ módulo TPM para gestionar el mantenimiento correctivo.",
    link_sistema: "https://alqos.vercel.app",
    creado_por: usuario?.nombre || "Sistema",
    fecha: new Date().toLocaleString("es-AR"),
    modulo: "TPM Mantenimiento",
  });
  return notif;
}
/**
 * Notifica cuando se crea un nuevo usuario
 * @param {object} nuevoUsuario — datos del usuario creado
 * @param {string} passwordTemporal — contraseña asignada
 */
export async function notificarNuevoUsuario(nuevoUsuario, passwordTemporal) {
  guardarNotificacion({
    tipo: "nuevo_usuario",
    titulo: `👤 Nuevo usuario creado: ${nuevoUsuario.nombre}`,
    mensaje: `Se creó el usuario ${nuevoUsuario.email} con rol ${nuevoUsuario.rol} para ${nuevoUsuario.empresa}.`,
    idRelacionado: nuevoUsuario.email,
    modulo: "Usuarios",
    color: "#7B5BF2",
  });
  await enviarEmail({
    to_email: nuevoUsuario.email,
    to_name: nuevoUsuario.nombre,
    subject: `Bienvenida a ALQOS™ — Tus credenciales de acceso`,
    titulo_notif: `Bienvenida a ALQOS™, ${nuevoUsuario.nombre}`,
    mensaje_principal: `Tu cuenta en ALQOS™ Sistema de Gestión Integral ha sido creada.`,
    detalle: `Email: ${nuevoUsuario.email} | Contraseña: ${passwordTemporal} | Rol: ${nuevoUsuario.rol} | Empresa: ${nuevoUsuario.empresa}`,
    accion_requerida: "Ingresá a alqos.vercel.app con tus credenciales. Te recomendamos cambiar tu contraseña al iniciar sesión.",
    link_sistema: "https://alqos.vercel.app",
    creado_por: "Administración ALQOS™",
    fecha: new Date().toLocaleString("es-AR"),
    modulo: "Gestión de Usuarios",
  });
}
/**
 * Verifica y notifica todas las acciones correctivas vencidas o por vencer (próximos 3 días)
 * Llamar al inicio de sesión del administrador
 */
export async function verificarVencimientos(emailAdmin) {
  try {
    const acs = JSON.parse(localStorage.getItem("alqos_acciones_correctivas") || "[]");
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const en3dias = new Date(hoy);
    en3dias.setDate(en3dias.getDate() + 3);
    // Limpiar antes de regenerar — sin esto, cada login (solo para Administrador) agregaba
    // una notificación más por cada Acción Correctiva vencida, sin borrar nunca las de logins
    // anteriores. Mismo patrón que se corrigió en verificarAlertasOperaciones().
    limpiarNotificacionesTipo("ac_vencida");
    limpiarNotificacionesTipo("ac_por_vencer");
    let count = 0;
    for (const ac of acs) {
      if (ac.estado === "cerrada") continue;
      if (!ac.fechaCompromiso) continue;
      const fecha = new Date(ac.fechaCompromiso);
      if (fecha <= en3dias) {
        await notificarACVencida(ac, emailAdmin);
        count++;
      }
    }
    if (count > 0) LOG.info(`Vencimientos verificados: ${count} acciones`, {});
    return count;
  } catch (err) { LOG.error("Error verificar vencimientos", err); return 0; }
}
/**
 * Verifica alertas de OTs, materiales y planchas
 * Llamar al login o cada 30 min
 */
export function verificarAlertasOperaciones() {
  try {
    const ots      = JSON.parse(localStorage.getItem("alqos_ots") || "[]");
    const ingresos = JSON.parse(localStorage.getItem("alqos_ingreso_materiales") || "[]");
    const pallets  = JSON.parse(localStorage.getItem("alqos_stock_pallets") || "[]");
    const planchas = JSON.parse(localStorage.getItem("alqos_preprensa_planchas") || "[]");
    const hoy      = new Date(); hoy.setHours(0,0,0,0);
    const mesActual = new Date().toISOString().slice(0,7);
    // OTs con entrega vencida
    const otsVencidas = ots.filter(o =>
      o.fechaEntrega && new Date(o.fechaEntrega) < hoy &&
      !["entregado","rechazado"].includes(o.estado)
    );
    limpiarNotificacionesTipo("ot_vencida");
    if (otsVencidas.length > 0) {
      guardarNotificacion({
        tipo: "ot_vencida",
        titulo: `⏰ ${otsVencidas.length} OT${otsVencidas.length>1?"s":""} con entrega vencida`,
        mensaje: otsVencidas.slice(0,3).map(o=>`${o.op} — ${o.cliente||"Sin cliente"}`).join(", ") + (otsVencidas.length>3?` y ${otsVencidas.length-3} más`:""),
        modulo: "OT",
        color: "#e63946",
      });
    }
    // OTs que vencen en 2 días
    const en2dias = new Date(hoy); en2dias.setDate(en2dias.getDate()+2);
    const otsPorVencer = ots.filter(o =>
      o.fechaEntrega &&
      new Date(o.fechaEntrega) >= hoy &&
      new Date(o.fechaEntrega) <= en2dias &&
      !["entregado","rechazado"].includes(o.estado)
    );
    limpiarNotificacionesTipo("ot_por_vencer");
    if (otsPorVencer.length > 0) {
      guardarNotificacion({
        tipo: "ot_por_vencer",
        titulo: `⚠️ ${otsPorVencer.length} OT${otsPorVencer.length>1?"s":""} vence${otsPorVencer.length===1?"":"n"} en 2 días`,
        mensaje: otsPorVencer.map(o=>`${o.op} — ${o.cliente||""} (${o.fechaEntrega})`).join(", "),
        modulo: "OT",
        color: "#f59e0b",
      });
    }
    // Materiales en cuarentena
    const enCuarentena = ingresos.filter(i => i.estado === "cuarentena");
    limpiarNotificacionesTipo("material_cuarentena");
    if (enCuarentena.length > 0) {
      guardarNotificacion({
        tipo: "material_cuarentena",
        titulo: `⏳ ${enCuarentena.length} material${enCuarentena.length>1?"es":""} en cuarentena`,
        mensaje: enCuarentena.slice(0,3).map(i=>`${i.codigoIngreso} — ${i.tipoMaterial} de ${i.proveedor}`).join(", "),
        modulo: "IngresoMateriales",
        color: "#f59e0b",
      });
    }
    // Pallets agotados
    const agotados = pallets.filter(p => p.estado === "agotado");
    limpiarNotificacionesTipo("pallet_agotado");
    if (agotados.length > 0) {
      guardarNotificacion({
        tipo: "pallet_agotado",
        titulo: `📦 ${agotados.length} pallet${agotados.length>1?"s":""} agotado${agotados.length>1?"s":""}`,
        mensaje: agotados.slice(0,3).map(p=>`${p.codigoPallet} — ${p.tipoMaterial}`).join(", "),
        modulo: "Stock",
        color: "#94a3b8",
      });
    }
    // Reimpresiones del mes > 5
    const reimprMes = planchas.filter(p => p.fecha?.startsWith(mesActual) && !p.esPrimera);
    limpiarNotificacionesTipo("planchas_reimp");
    if (reimprMes.length >= 5) {
      guardarNotificacion({
        tipo: "planchas_reimp",
        titulo: `🖨️ ${reimprMes.length} reimpresiones de planchas este mes`,
        mensaje: `Costo acumulado: $${reimprMes.reduce((s,p)=>s+parseFloat(p.costo||0),0).toFixed(2)}. Revisá Pre Prensa para identificar causas.`,
        modulo: "PrePrensa",
        color: "#e63946",
      });
    }
    // TPM — órdenes vencidas
    const tpm = JSON.parse(localStorage.getItem("alqos_tpm") || "[]");
    const tpmVencidas = tpm.filter(t => t.fechaProgramada && new Date(t.fechaProgramada) < hoy && t.estado !== "completado" && t.estado !== "cancelado");
    limpiarNotificacionesTipo("tpm_vencido");
    if (tpmVencidas.length > 0) {
      guardarNotificacion({
        tipo: "tpm_vencido",
        titulo: `🔧 ${tpmVencidas.length} orden${tpmVencidas.length>1?"es":""} TPM vencida${tpmVencidas.length>1?"s":""}`,
        mensaje: tpmVencidas.slice(0,3).map(t=>`${t.idTPM} — ${t.maquina}`).join(", "),
        modulo: "TPM",
        color: "#e63946",
      });
    }
    // Produccion — registros con problemas detectados sin resolver
    const produccion = JSON.parse(localStorage.getItem("alqos_produccion") || "[]");
    const prodProblemas = produccion.filter(p => p.problemasDetectados?.trim() && p.estado !== "finalizada" && p.estado !== "rechazada");
    limpiarNotificacionesTipo("produccion_problema");
    if (prodProblemas.length > 0) {
      guardarNotificacion({
        tipo: "produccion_problema",
        titulo: `🏗️ ${prodProblemas.length} registro${prodProblemas.length>1?"s":""} de producción con problemas`,
        mensaje: prodProblemas.slice(0,3).map(p=>`${p.codigo} — ${p.maquina||""}`).join(", "),
        modulo: "Produccion",
        color: "#f59e0b",
      });
    }
    // Ventas — cotizaciones por vencer en 3 días
    const ventas = JSON.parse(localStorage.getItem("alqos_ventas") || "[]");
    const en3dias = new Date(hoy); en3dias.setDate(en3dias.getDate() + 3);
    const ventasPorVencer = ventas.filter(v => v.fechaEntrega && new Date(v.fechaEntrega) <= en3dias && new Date(v.fechaEntrega) >= hoy && !["entregada","perdida"].includes(v.estado));
    limpiarNotificacionesTipo("ventas_por_vencer");
    if (ventasPorVencer.length > 0) {
      guardarNotificacion({
        tipo: "ventas_por_vencer",
        titulo: `📈 ${ventasPorVencer.length} venta${ventasPorVencer.length>1?"s":""} con entrega próxima`,
        mensaje: ventasPorVencer.slice(0,3).map(v=>`${v.codigo} — ${v.cliente||""}`).join(", "),
        modulo: "Ventas",
        color: "#f59e0b",
      });
    }
  } catch(e) { console.error("Error verificarAlertasOperaciones", e); }
}
/**
 * Verifica el punto de reposición de todos los materiales marcados como críticos
 * y genera una alerta si alguno está en o por debajo del punto de reposición.
 * Llamar junto con verificarAlertasOperaciones() (mismo lugar, mismo momento).
 */
export function verificarAlertasReposicion() {
  try {
    const pallets = JSON.parse(localStorage.getItem("alqos_stock_pallets") || "[]");
    const movs = JSON.parse(localStorage.getItem("alqos_stock_movimientos") || "[]");
    const configs = JSON.parse(localStorage.getItem("alqos_config_materiales") || "[]");
    const alertas = calcularTodasLasAlertasReposicion(pallets, movs, configs);
    limpiarNotificacionesTipo("material_bajo_stock");
    const criticos = alertas.filter(a => a.estado === "critico");
    if (criticos.length > 0) {
      guardarNotificacion({
        tipo: "material_bajo_stock",
        titulo: `🎯 ${criticos.length} material${criticos.length > 1 ? "es" : ""} en punto de reposición`,
        mensaje: criticos.slice(0, 3).map(a => `${a.tipoMaterial} (${Math.round(a.stockActual)} / mínimo ${Math.round(a.puntoReorden)})`).join(", ") + (criticos.length > 3 ? ` y ${criticos.length - 3} más` : ""),
        modulo: "Stock",
        color: "#e63946",
      });
    }
    return criticos.length;
  } catch (e) { LOG.error("Error verificar alertas de reposición", e); return 0; }
}
/**
 * Notifica, de forma agregada (una sola notificación por publicación de ALQOS
 * News, actualizada en vez de acumulada cada vez que alguien reacciona), cuántas
 * reacciones tiene cada novedad publicada. Reusa limpiarNotificacionesTipo() con
 * un tipo por-publicación ("alqos_news_reacciones_<id>") para no repetir el bug
 * de Fase E (notificaciones que se acumulaban sin límite en OT/Acciones
 * Correctivas porque cada chequeo generaba una nueva sin borrar la anterior).
 * El total ya notificado se guarda en el propio registro de alqos_news
 * (reaccionesNotificadas) para no re-notificar si el conteo no cambió.
 * Llamar al cargar Escritorio Calidad (tab Centro de Mando) — mismo patrón que
 * verificarAlertasOperaciones().
 */
export function verificarNotificacionesReacciones() {
  try {
    const noticias = JSON.parse(localStorage.getItem("alqos_news") || "[]");
    let huboCambios = false;
    const actualizadas = noticias.map(n => {
      if (n.estado !== "publicado") return n;
      const total = Object.keys(n.reacciones || {}).length;
      if (total === 0 || total === (n.reaccionesNotificadas || 0)) return n;
      limpiarNotificacionesTipo(`alqos_news_reacciones_${n.id}`);
      guardarNotificacion({
        tipo: `alqos_news_reacciones_${n.id}`,
        titulo: `📰 Reacciones en ALQOS News`,
        mensaje: `La novedad "${n.titulo}" tiene ${total} reacción${total === 1 ? "" : "es"}.`,
        idRelacionado: String(n.id),
        modulo: "AlqosNews",
        color: "#2A7FFF",
      });
      huboCambios = true;
      return { ...n, reaccionesNotificadas: total };
    });
    if (huboCambios) {
      localStorage.setItem("alqos_news", JSON.stringify(actualizadas));
      subirDatos("alqos_news");
    }
  } catch (e) { LOG.error("Error verificando notificaciones de reacciones", e); }
}
export async function syncNotificacionesDesdeSupabase() {
  try {
    const { data, error } = await supabase.from("notificaciones").select("data").order("id", { ascending: false }).limit(200);
    if (error || !data) return;
    const remotas = data.map(r => r.data);
    const locales = JSON.parse(localStorage.getItem(STORAGE_NOTIF) || "[]");
    const map = {};
    [...remotas, ...locales].forEach(x => { if (x && x.id) map[x.id] = map[x.id] ? { ...x, leida: x.leida || map[x.id].leida } : x; });
    const merge = Object.values(map).sort((a,b) => b.id - a.id).slice(0, 200);
    localStorage.setItem(STORAGE_NOTIF, JSON.stringify(merge));
    window.dispatchEvent(new Event("alqos-notif-nueva"));
  } catch (e) { console.warn("sync notif:", e); }
}
